import type { PickemQuestion } from '@low6-pickem/api-types';
import type { TeamPair } from '@/types/domain';
import { getOptionId } from './id-helpers';
import { findOptionByTitle } from './id-helpers';
import { getLocalizedField } from './localized-field';
import { getSportLogoUrl, getFileExtForSport } from '@low6-pickem/sports-config';
import { normalizeTeamLogoUrl } from './normalizeTeamLogoUrl';
import i18n from '@/i18n';

// Re-export TeamPair for backwards compatibility
export type { TeamPair };

const COLORS = [
  { color: '#c8102e', accent: '#000000' },
  { color: '#003f7f', accent: '#ffffff' },
  { color: '#006847', accent: '#ffffff' },
  { color: '#fcb514', accent: '#000000' },
];

// Map stat type codes to display names (single source — used by both live stat display and extractStatData)
const statTypeDisplayMap: Record<string, string> = {
  // NFL stats
  rushing_yards: 'Rushing Yards',
  passing_yards: 'Passing Yards',
  receptions: 'Receptions',
  receiving_yards: 'Receiving Yards',
  touchdowns: 'Touchdowns',
  passing_touchdowns: 'Passing TDs',
  rushing_touchdowns: 'Rushing TDs',
  interceptions: 'Interceptions',
  fumbles: 'Fumbles',
  sacks: 'Sacks',
  tackles: 'Tackles',
  // NBA stats
  points: 'Points',
  assists: 'Assists',
  rebounds: 'Rebounds',
  steals: 'Steals',
  blocks: 'Blocks',
  blocked_att: 'Blocked Attempts',
  blocked_shots: 'Blocked Shots',
  turnovers: 'Turnovers',
  three_points_made: '3-Pointers Made',
  three_points_att: '3-Point Attempts',
  three_points_pct: '3-Point %',
  two_points_made: '2-Pointers Made',
  two_points_att: '2-Point Attempts',
  two_points_pct: '2-Point %',
  free_throws_made: 'Free Throws Made',
  free_throws_att: 'Free Throw Attempts',
  free_throws_pct: 'Free Throw %',
  field_goals_made: 'Field Goals Made',
  field_goals_att: 'Field Goal Attempts',
  field_goals_pct: 'Field Goal %',
  minutes: 'Minutes',
  offensive_rebounds: 'Offensive Rebounds',
  defensive_rebounds: 'Defensive Rebounds',
  personal_fouls: 'Personal Fouls',
  plus_minus: 'Plus/Minus',
  double_doubles: 'Double Doubles',
  triple_doubles: 'Triple Doubles',
  // Other sports
  goals: 'Goals',
  runs: 'Runs',
  hits: 'Hits',
  home_runs: 'Home Runs',
  rbi: 'RBIs',
  strikeouts: 'Strikeouts',
  // Soccer stats (codes are `soccer_`-prefixed; names from SOCCER_STAT_TYPES)
  soccer_goals: 'Goals',
  soccer_assists: 'Assists',
  soccer_shots: 'Shots',
  soccer_shots_on_target: 'Shots on Target',
  soccer_passes: 'Passes',
  soccer_pass_accuracy: 'Pass Accuracy',
  soccer_tackles: 'Tackles',
  soccer_clearances: 'Clearances',
  soccer_yellow_card: 'Yellow Cards',
  soccer_red_card: 'Red Cards',
  soccer_minutes_played: 'Minutes Played',
  soccer_saves: 'Saves',
  soccer_corners: 'Corners',
  soccer_fouls: 'Fouls',
  soccer_possession: 'Possession',
  soccer_team_goals: 'Team Goals',
  soccer_team_shots: 'Team Shots',
  soccer_team_shots_on_target: 'Team Shots on Target',
  soccer_team_yellow_cards: 'Team Yellow Cards',
  soccer_team_red_cards: 'Team Red Cards',
};

type OptionToTeamPairConfig = {
  idPrefix: string;
  resolveName: (
    option: PickemQuestion['options'][0] | undefined,
    index: number,
    locale: string
  ) => string;
};

function buildOptionAsTeamPair(
  option: PickemQuestion['options'][0] | undefined,
  index: number,
  question: PickemQuestion | undefined,
  config: OptionToTeamPairConfig
): TeamPair {
  const colorPair = COLORS[index % COLORS.length];
  const locale = i18n.language;

  // Extract player position - prefer option.grading_data, fallback to question metadata
  const position =
    option?.grading_data?.playerPosition ||
    question?.question_metadata?.grading?.config?.playerPosition;

  // Extract fixture data - prefer option.grading_data.fixtureData, fallback to question.sport_match_data
  const fixtureData = option?.grading_data?.fixtureData;
  const matchData = question?.sport_match_data;
  let fixture:
    | {
        homeTeam: string;
        awayTeam: string;
        homeTeamAbbreviation: string;
        awayTeamAbbreviation: string;
        matchTime: string;
      }
    | undefined = undefined;
  let isPlayerTeam: 'home' | 'away' | undefined = undefined;

  if (fixtureData) {
    fixture = {
      homeTeam: fixtureData.homeTeamName,
      awayTeam: fixtureData.awayTeamName,
      homeTeamAbbreviation: fixtureData.homeTeamAbbreviation,
      awayTeamAbbreviation: fixtureData.awayTeamAbbreviation,
      matchTime: formatMatchTime(fixtureData.scheduledDate),
    };

    const teamId = option?.grading_data?.teamId;
    if (teamId !== undefined && teamId !== null) {
      if (fixtureData.homeTeamId === String(teamId)) {
        isPlayerTeam = 'home';
      } else if (fixtureData.awayTeamId === String(teamId)) {
        isPlayerTeam = 'away';
      }
    }
  } else if (matchData) {
    fixture = {
      homeTeam: matchData.homeTeamName,
      awayTeam: matchData.awayTeamName,
      homeTeamAbbreviation: matchData.homeTeamAbbreviation,
      awayTeamAbbreviation: matchData.awayTeamAbbreviation,
      matchTime: formatMatchTime(matchData.scheduledDate),
    };

    const teamId = option?.grading_data?.teamId;
    if (teamId !== undefined && teamId !== null) {
      if (matchData.homeTeamId === String(teamId)) {
        isPlayerTeam = 'home';
      } else if (matchData.awayTeamId === String(teamId)) {
        isPlayerTeam = 'away';
      }
    }
  }

  const statTypeCode = question?.stat_type_code;
  const liveStatType = statTypeCode
    ? i18n.t(`stats.${statTypeCode}`, {
        defaultValue: statTypeDisplayMap[statTypeCode] || statTypeCode,
      })
    : undefined;

  const normalizeAssetUrl = (url?: string | null) => (url ? normalizeTeamLogoUrl(url) : url);

  // Team tri-code the player plays for. Populated by the question factories
  // when a question is feed-based; falls back to the question-level metadata
  // shortcode if option-level isn't set.
  const teamShortcode =
    option?.grading_data?.teamShortcode ||
    question?.question_metadata?.grading?.config?.teamShortcode;

  return {
    id: option ? getOptionId(option) : `${config.idPrefix}${index + 1}`,
    name: config.resolveName(option, index, locale),
    color: colorPair.color,
    accent: colorPair.accent,
    jerseyUrl: normalizeAssetUrl(option?.option_image),
    thirdImageUrl: normalizeAssetUrl(option?.option_third_image),
    popularityPct: option?.popularity_pct,
    position,
    teamShortcode,
    fixture,
    isPlayerTeam,
    liveValue: option?.grading_data?.liveValue,
    liveStatType,
  };
}

function createTeamOption(
  option: PickemQuestion['options'][0] | undefined,
  index: number,
  question?: PickemQuestion
): TeamPair {
  return buildOptionAsTeamPair(option, index, question, {
    idPrefix: 'team',
    resolveName: (opt, i, locale) =>
      opt
        ? (getLocalizedField(opt, 'title', locale) ?? i18n.t('game.fallbackTeam', { n: i + 1 }))
        : i18n.t('game.fallbackTeam', { n: i + 1 }),
  });
}

function createPlayerOption(
  option: PickemQuestion['options'][0] | undefined,
  index: number,
  question?: PickemQuestion
): TeamPair {
  return buildOptionAsTeamPair(option, index, question, {
    idPrefix: 'player',
    resolveName: (opt, i, locale) => {
      const title = opt ? (getLocalizedField(opt, 'title', locale) ?? '') : '';
      return opt
        ? formatPlayerNameAsInitialSurname(title) || i18n.t('game.fallbackPlayer', { n: i + 1 })
        : i18n.t('game.fallbackPlayer', { n: i + 1 });
    },
  });
}

export function getTeamsForQuestion(question: PickemQuestion): TeamPair[] {
  // Return ALL options, not just the first 2
  return question.options.map((option, index) => createTeamOption(option, index, question));
}

export function getPlayersForQuestion(question: PickemQuestion): TeamPair[] {
  return question.options.map((option, index) => createPlayerOption(option, index, question));
}

/**
 * Custom option format for CustomPick component
 */
export interface CustomOptionData {
  id: string;
  label: string;
  imageUrl?: string | null;
  popularityPct?: number;
  thirdImageUrl?: string | null;
}

/**
 * Get options for custom question type in CustomPick format
 */
export function getCustomOptionsForQuestion(question: PickemQuestion): CustomOptionData[] {
  const locale = i18n.language;
  return question.options.map((option, index) => ({
    id: getOptionId(option),
    label:
      getLocalizedField(option, 'title', locale) ||
      getLocalizedField(option, 'option_text', locale) ||
      i18n.t('game.fallbackOption', { n: index + 1 }),
    imageUrl: option.option_image ? normalizeTeamLogoUrl(option.option_image) : null,
    popularityPct: option.popularity_pct,
    thirdImageUrl: option.option_third_image
      ? normalizeTeamLogoUrl(option.option_third_image)
      : null,
  }));
}

export function getPercentagesForPropQuestion(question: PickemQuestion) {
  const yesOption = question.options.find(opt => opt.title_en?.toLowerCase().includes('yes'));
  const noOption = question.options.find(opt => opt.title_en?.toLowerCase().includes('no'));

  return {
    yes: yesOption?.popularity_pct == undefined ? 50 : yesOption?.popularity_pct,
    no: noOption?.popularity_pct == undefined ? 50 : noOption?.popularity_pct,
  };
}

export function getPercentagesForOverUnderQuestion(question: PickemQuestion) {
  const overOption = question.options.find(opt => opt.title_en?.toLowerCase().includes('over'));
  const underOption = question.options.find(opt => opt.title_en?.toLowerCase().includes('under'));

  return {
    over: overOption?.popularity_pct == undefined ? 50 : overOption?.popularity_pct,
    under: underOption?.popularity_pct == undefined ? 50 : underOption?.popularity_pct,
  };
}

export function getThirdImagesForPropQuestion(question: PickemQuestion) {
  const yesOption = question.options.find(opt => opt.title_en?.toLowerCase().includes('yes'));
  const noOption = question.options.find(opt => opt.title_en?.toLowerCase().includes('no'));

  return {
    yes: yesOption?.option_third_image
      ? normalizeTeamLogoUrl(yesOption.option_third_image)
      : undefined,
    no: noOption?.option_third_image
      ? normalizeTeamLogoUrl(noOption.option_third_image)
      : undefined,
  };
}

export function findOptionByAnswer(
  question: PickemQuestion,
  answer: 'yes' | 'no'
): string | undefined {
  // First try to find by title containing yes/no
  let option = findOptionByTitle(question, answer);

  // If not found, map by position: first option = yes, second = no
  if (!option && question.options.length >= 2) {
    option = answer === 'yes' ? question.options[0] : question.options[1];
  }

  return option ? getOptionId(option) : undefined;
}

/**
 * Formats a full name as "Initial. Surname" (e.g. "Patrick Mahomes" -> "P. Mahomes").
 * Single-word names are returned as-is; empty string returns empty.
 */
export function formatPlayerNameAsInitialSurname(fullName: string): string {
  const trimmed = fullName.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return trimmed;
  const initial = parts[0].charAt(0).toUpperCase();
  const surname = parts[parts.length - 1];
  return `${initial}. ${surname}`;
}

/**
 * Extracts entity data (player or team) from OVER_UNDER question metadata
 * Handles both player over/under and team over/under questions
 * Falls back to option-level grading_data for backwards compatibility
 */
export function extractPlayerData(question: PickemQuestion): {
  name: string;
  position: string;
} {
  // First try question-level metadata (new format)
  const config = question.question_metadata?.grading?.config;

  // Check for player data first
  if (config?.playerName) {
    return {
      name: config.playerName,
      position: config.playerPosition || '',
    };
  }

  // Check for team data in config (team over/under questions)
  if (config?.teamName) {
    return {
      name: config.teamName,
      position: '', // Teams don't have positions
    };
  }

  // Fallback to option-level grading_data (always populated by factory)
  const firstOption = question.options?.[0];
  const gradingData = firstOption?.grading_data;

  // Check for player data in option
  if (gradingData?.playerName) {
    return {
      name: gradingData.playerName,
      position: gradingData.playerPosition || '',
    };
  }

  // Check for team data in option (team over/under questions)
  if (gradingData?.teamName) {
    return {
      name: gradingData.teamName,
      position: '', // Teams don't have positions
    };
  }

  return {
    name: i18n.t('game.unknownPlayer'),
    position: '',
  };
}

/**
 * Extracts stat data from OVER_UNDER question metadata and subtext
 * Falls back to option-level grading_data and question.margin for backwards compatibility
 */
export function extractStatData(question: PickemQuestion): {
  threshold: number;
  displayName: string;
  type: string;
} {
  // First try question-level metadata (new format)
  const config = question.question_metadata?.grading?.config;

  if (config?.threshold !== undefined) {
    const statType = config.statType || '';
    return {
      threshold: config.threshold,
      displayName: i18n.t(`stats.${statType}`, {
        defaultValue: statTypeDisplayMap[statType] || statType,
      }),
      type: statType,
    };
  }

  // Fallback to option-level grading_data (always populated by factory)
  const firstOption = question.options?.[0];
  const gradingData = firstOption?.grading_data;
  if (gradingData?.threshold !== undefined) {
    // Get stat type from question.stat_type_code field
    const statType = question.stat_type_code || '';
    return {
      threshold: gradingData.threshold,
      displayName: i18n.t(`stats.${statType}`, {
        defaultValue: statTypeDisplayMap[statType] || statType,
      }),
      type: statType,
    };
  }

  // Final fallback to question.margin field
  if (question.margin !== undefined && question.margin !== null) {
    const statType = question.stat_type_code || '';
    return {
      threshold: question.margin,
      displayName: i18n.t(`stats.${statType}`, {
        defaultValue: statTypeDisplayMap[statType] || statType,
      }),
      type: statType,
    };
  }

  return {
    threshold: 0,
    displayName: '',
    type: '',
  };
}

/**
 * Formats match scheduled date to display format (e.g., "Sun, 1:00pm")
 */
export function formatMatchTime(scheduledDate?: string): string {
  if (!scheduledDate) return '';

  const date = new Date(scheduledDate);
  const locale = i18n.language === 'fr' ? 'fr-CA' : 'en-US';
  const dayOfWeek = date.toLocaleDateString(locale, { weekday: 'short' });
  const time = date.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: i18n.language !== 'fr',
  });

  return `${dayOfWeek}, ${time}`;
}

/**
 * Gets the appropriate league logo URL based on the sport
 * @param sport - The sport identifier ('NFL', 'NBA', 'NHL')
 * @returns The league logo URL
 */
export function getLeagueLogoUrl(sport?: string): string {
  return getSportLogoUrl(sport);
}

/**
 * Extracts the player's team logo URL from OVER_UNDER question metadata
 * Used to display team badge in player over/under questions
 * Falls back to generating URL from teamShortcode if teamLogoUrl isn't present
 */
export function extractPlayerTeamLogo(question: PickemQuestion): string {
  // First try question-level metadata (new format with teamLogoUrl)
  const config = question.question_metadata?.grading?.config;

  // Only show team badge for Player Over/Under (when there's a playerName)
  // For Team Over/Under, the main image IS the team logo, so don't show badge
  if (!config?.playerName && config?.teamName) {
    return ''; // Team Over/Under - no badge needed
  }

  if (config?.teamLogoUrl) {
    return normalizeTeamLogoUrl(config.teamLogoUrl);
  }

  // Fallback: try to generate URL from teamShortcode in config
  if (config?.teamShortcode) {
    const sport = question.sport_external_id?.toLowerCase() || 'nba';
    return generateTeamLogoUrl(sport, config.teamShortcode);
  }

  // Fallback: try option-level grading_data
  const firstOption = question.options?.[0];
  const gradingData = firstOption?.grading_data;
  if (gradingData?.teamShortcode) {
    const sport = question.sport_external_id?.toLowerCase() || 'nba';
    return generateTeamLogoUrl(sport, gradingData.teamShortcode);
  }

  return '';
}

/**
 * Generates a team logo URL from sport and team shortcode
 * Uses the same blob storage structure as the backend
 */
function generateTeamLogoUrl(sport: string, teamShortcode: string): string {
  const STORAGE_ACCOUNT = import.meta.env.VITE_BLOB_STORAGE_ACCOUNT_NAME || 'draftkingsdev';
  const CONTAINER = import.meta.env.VITE_BLOB_STORAGE_CONTAINER || 'picks-core';
  const normalizedSport = sport.toLowerCase();
  const normalizedShortcode = teamShortcode.toUpperCase();

  // File extension varies by sport (matches backend DraftKingsTeamLogoProvider)
  const fileExt = getFileExtForSport(normalizedSport);

  return `https://${STORAGE_ACCOUNT}.blob.core.windows.net/${CONTAINER}/${normalizedSport}/teams/${normalizedShortcode}.${fileExt}`;
}

/**
 * Extracts jersey/player image URL from OVER_UNDER question options
 * The jersey image is stored in option_third_image (or option_image as fallback)
 */
export function extractJerseyUrl(question: PickemQuestion): string {
  // Try option's third image first (jersey)
  const firstOption = question.options?.[0];
  if (firstOption?.option_third_image) {
    return normalizeTeamLogoUrl(firstOption.option_third_image);
  }

  // Fallback to option image
  if (firstOption?.option_image) {
    return normalizeTeamLogoUrl(firstOption.option_image);
  }

  // Final fallback to question image
  return question.main_image || '';
}

/**
 * Extracts team logo URLs from OVER_UNDER question
 * Checks sport_match_data first, then falls back to option grading_data
 */
export function extractTeamLogos(question: PickemQuestion): {
  homeTeamLogo: string;
  awayTeamLogo: string;
} {
  // First try sport_match_data
  if (question.sport_match_data?.homeTeamLogo || question.sport_match_data?.awayTeamLogo) {
    return {
      homeTeamLogo: question.sport_match_data.homeTeamLogo
        ? normalizeTeamLogoUrl(question.sport_match_data.homeTeamLogo)
        : '',
      awayTeamLogo: question.sport_match_data.awayTeamLogo
        ? normalizeTeamLogoUrl(question.sport_match_data.awayTeamLogo)
        : '',
    };
  }

  // Fallback: try to get from option grading_data fixtureData
  const firstOption = question.options?.[0];
  const fixtureData = firstOption?.grading_data?.fixtureData;
  if (fixtureData) {
    // Note: fixtureData may have team names but not logos
    // This is a structural limitation - logos need to come from sport_match_data
    return {
      homeTeamLogo: '',
      awayTeamLogo: '',
    };
  }

  return {
    homeTeamLogo: '',
    awayTeamLogo: '',
  };
}

export function extractTeamShortCodes(question: PickemQuestion): {
  homeTeamAbbreviation: string;
  awayTeamAbbreviation: string;
} {
  return {
    homeTeamAbbreviation: question.sport_match_data?.homeTeamAbbreviation || '',
    awayTeamAbbreviation: question.sport_match_data?.awayTeamAbbreviation || '',
  };
}

/**
 * Extracts team names from OVER_UNDER question for matchup display
 * Checks sport_match_data first, then falls back to option grading_data fixtureData
 */
export function extractTeamNames(question: PickemQuestion): {
  homeTeamName: string;
  awayTeamName: string;
} {
  // First try sport_match_data
  if (question.sport_match_data?.homeTeamName || question.sport_match_data?.awayTeamName) {
    return {
      homeTeamName: question.sport_match_data.homeTeamName || '',
      awayTeamName: question.sport_match_data.awayTeamName || '',
    };
  }

  // Fallback: try to get from option grading_data fixtureData
  const firstOption = question.options?.[0];
  const fixtureData = firstOption?.grading_data?.fixtureData;
  if (fixtureData) {
    return {
      homeTeamName: fixtureData.homeTeamName || '',
      awayTeamName: fixtureData.awayTeamName || '',
    };
  }

  return {
    homeTeamName: '',
    awayTeamName: '',
  };
}

/**
 * Extracts match time from OVER_UNDER question
 * Checks sport_match_data first, then falls back to option grading_data fixtureData
 */
export function extractMatchTime(question: PickemQuestion): string {
  // First try sport_match_data
  if (question.sport_match_data?.scheduledDate) {
    return formatMatchTime(question.sport_match_data.scheduledDate);
  }

  // Fallback: try to get from option grading_data fixtureData
  const firstOption = question.options?.[0];
  const fixtureData = firstOption?.grading_data?.fixtureData;
  if (fixtureData?.scheduledDate) {
    return formatMatchTime(fixtureData.scheduledDate);
  }

  return '';
}
